import { useEffect, useRef } from "react";

interface Point {
  offsetX: number;
  velocityX: number;
  offsetY: number;
  velocityY: number;
  char: string;
}

interface Column {
  x: number;
  points: Point[];
}

const CHAR_POOL =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$%&*+=?/";

const TEXT_COLOR = "74, 55, 40"; // rgb components of #4a3728

const ROW_SPACING = 21;
const COLUMN_SPACING = 34;
const FONT_SIZE = 18;

// Heavy bead-chain feel: strong link tension, very slow restore, high damping
const TENSION = 0.24;
const RESTORE = 0.006;
const DAMPING = 0.93;

// Mouse interaction
const INFLUENCE_RADIUS = 110;
const MOUSE_FORCE_SCALE = 0.35;   // horizontal push from mouse motion
const LIFT_FORCE = 3.2;           // vertical lift when hovering
const HOVER_LIFT_THRESHOLD = 0.05;

export default function CharacterCurtain() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    let width = wrapper.clientWidth;
    let height = wrapper.clientHeight;
    let columns: Column[] = [];
    let topY = 0;

    const buildColumns = () => {
      const columnCount = 12 + Math.floor(Math.random() * 8); // 12 - 19
      const totalColumnsWidth = (columnCount - 1) * COLUMN_SPACING;
      const startX = width / 2 - totalColumnsWidth / 2;

      const cols: Column[] = [];
      let maxCharCount = 0;
      for (let c = 0; c < columnCount; c++) {
        const charCount = 44 + Math.floor(Math.random() * 16); // 44 - 59
        if (charCount > maxCharCount) maxCharCount = charCount;
        const points: Point[] = [];
        for (let i = 0; i < charCount; i++) {
          points.push({
            offsetX: 0,
            velocityX: 0,
            offsetY: 0,
            velocityY: 0,
            char: CHAR_POOL[Math.floor(Math.random() * CHAR_POOL.length)],
          });
        }
        cols.push({ x: startX + c * COLUMN_SPACING, points });
      }
      const totalHeight = (maxCharCount - 1) * ROW_SPACING;
      topY = (height - totalHeight) / 2;
      return cols;
    };

    const resize = () => {
      width = wrapper.clientWidth;
      height = wrapper.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      if (columns.length > 0) {
        const columnCount = columns.length;
        const totalColumnsWidth = (columnCount - 1) * COLUMN_SPACING;
        const startX = width / 2 - totalColumnsWidth / 2;
        let maxCharCount = 0;
        columns.forEach((col, idx) => {
          col.x = startX + idx * COLUMN_SPACING;
          if (col.points.length > maxCharCount) maxCharCount = col.points.length;
        });
        const totalHeight = (maxCharCount - 1) * ROW_SPACING;
        topY = (height - totalHeight) / 2;
      }
    };

    resize();
    columns = buildColumns();

    let mouseX = -9999;
    let mouseY = -9999;
    let prevMouseX = -9999;
    let mouseVX = 0;
    let mouseInside = false;

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const nx = e.clientX - rect.left;
      const ny = e.clientY - rect.top;
      if (!mouseInside) {
        prevMouseX = nx;
      }
      mouseInside = true;
      mouseX = nx;
      mouseY = ny;
    };
    const onLeave = () => {
      mouseInside = false;
      mouseX = -9999;
      mouseY = -9999;
    };

    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);

    const onResize = () => resize();
    window.addEventListener("resize", onResize);

    let raf = 0;

    const tick = () => {
      // update mouse horizontal velocity
      if (mouseInside) {
        let vx = mouseX - prevMouseX;
        if (vx > 30) vx = 30;
        else if (vx < -30) vx = -30;
        mouseVX = vx;
        prevMouseX = mouseX;
      } else {
        mouseVX *= 0.85;
      }

      // physics
      for (const col of columns) {
        const pts = col.points;
        const n = pts.length;
        const snapX = new Array<number>(n);
        const snapY = new Array<number>(n);
        for (let i = 0; i < n; i++) {
          snapX[i] = pts[i].offsetX;
          snapY[i] = pts[i].offsetY;
        }

        // top point always anchored (chain hangs from the top)
        pts[0].offsetX = 0;
        pts[0].offsetY = 0;
        pts[0].velocityX = 0;
        pts[0].velocityY = 0;

        for (let i = 1; i < n; i++) {
          const curX = snapX[i];
          const curY = snapY[i];
          const upX = snapX[i - 1];
          const upY = snapY[i - 1];
          const downX = i + 1 < n ? snapX[i + 1] : curX;
          const downY = i + 1 < n ? snapY[i + 1] : curY;

          // chain tension pulls each bead toward its neighbors on both axes
          let accelX =
            TENSION * (upX - curX) + TENSION * (downX - curX) - RESTORE * curX;
          let accelY =
            TENSION * (upY - curY) + TENSION * (downY - curY) - RESTORE * curY;

          if (mouseInside) {
            const px = col.x + curX;
            const py = topY + i * ROW_SPACING + curY;
            const dx = mouseX - px;
            const dy = mouseY - py;
            const distSq = dx * dx + dy * dy;
            const rSq = INFLUENCE_RADIUS * INFLUENCE_RADIUS;
            if (distSq < rSq) {
              const dist = Math.sqrt(distSq);
              const falloff = 1 - dist / INFLUENCE_RADIUS;
              const f2 = falloff * falloff;

              // horizontal nudge from cursor motion
              if (Math.abs(mouseVX) > HOVER_LIFT_THRESHOLD) {
                accelX += mouseVX * f2 * MOUSE_FORCE_SCALE;
              }
              // vertical lift: push bead upward toward the cursor
              // (only lift when bead is below cursor, so we open a "hole")
              if (dy < 0) {
                accelY -= LIFT_FORCE * f2;
              } else {
                accelY -= LIFT_FORCE * f2 * 0.4;
              }
            }
          }

          const newVX = (pts[i].velocityX + accelX) * DAMPING;
          const newVY = (pts[i].velocityY + accelY) * DAMPING;
          pts[i].velocityX = newVX;
          pts[i].velocityY = newVY;
          pts[i].offsetX = curX + newVX;
          pts[i].offsetY = curY + newVY;
        }
      }

      // render (transparent canvas so page background image shows through).
      // NOTE: draw every single character — nothing is hidden or skipped.
      // Top rows visually sit behind the hat overlay (higher z-index),
      // so lifting the chain reveals the background naturally.
      ctx.clearRect(0, 0, width, height);
      ctx.font = `${FONT_SIZE}px "SF Mono", "JetBrains Mono", "Courier New", monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = `rgb(${TEXT_COLOR})`;

      for (const col of columns) {
        const pts = col.points;
        for (let i = 0; i < pts.length; i++) {
          const p = pts[i];
          const x = col.x + p.offsetX;
          const y = topY + i * ROW_SPACING + p.offsetY;
          ctx.fillText(p.char, x, y);
        }
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div ref={wrapperRef} className="relative w-full h-full">
      <canvas ref={canvasRef} className="block w-full h-full cursor-crosshair" />
    </div>
  );
}